import React, { useEffect, useRef } from 'react';
import { Box, Button, Spinner } from '@cloudscape-design/components';
import { useCopilot } from './CopilotContext';
import { CopilotMessageBubble } from './CopilotMessageBubble';

// Starter chips shown in the empty state. Aligned with the product feedback's
// "Astra Starter Chips" set. Each chip sends a concise, directive prompt that is
// scoped to the user's preferred site(s) and business unit and a recent window.
// The prompt doubles as the visible user message, so it is kept readable.
const SUGGESTED_PROMPTS = [
  {
    label: 'See what Astra can do',
    prompt:
      'What can you help me with? Give me a short, plain-language overview of how you can help with my audits, tasks, and findings — grouped by what I can ask for, not a list of tools.',
  },
  {
    label: 'Audit & task summary',
    prompt:
      'Give me a status summary of audits and tasks for my preferred site(s) over the last 3 months — totals and a breakdown by status, calling out expired audits and overdue tasks.',
  },
  {
    label: 'Audit insights & opportunities',
    prompt:
      'What do recent audits at my preferred site(s) reveal over the last 3 months? Summarize the recurring themes, repeat failures, and the biggest opportunities — synthesize the patterns rather than listing every record.',
  },
  {
    label: 'Upcoming & in-progress audits',
    prompt:
      'Show the audit pipeline for my preferred site(s): audits currently in progress, plus those scheduled or upcoming over the next 30 days. List them in date order and highlight anything due soon. Do not include completed audits.',
  },
  {
    label: 'Tasks that need attention',
    prompt:
      'Triage the tasks that need attention for my preferred site(s): lead with overdue corrective-action tasks (most overdue first) and repeat failures, then tasks due soon.',
  },
  {
    label: 'How my site is trending',
    prompt:
      'How is my preferred site trending this month vs. last month? Compare pass rate (compliance), audit volume, and open/overdue tasks, with direction and deltas. Compare time periods within my business unit only.',
  },
];

export function CopilotMessageList() {
  const { state, sendMessage } = useCopilot();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, state.isLoading]);

  if (state.messages.length === 0 && !state.isLoading) {
    return (
      <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 's' }}>
        <Box variant="h3">Welcome to Astra</Box>
        <Box variant="p" padding={{ bottom: 's' }}>
          I&apos;m here to help with audits, tasks, questions, and more across Gravion.
          Try one of these:
        </Box>
        <div className="copilot-suggested-prompts">
          {SUGGESTED_PROMPTS.map(({ label, prompt }) => (
            <div key={label} className="copilot-suggested-prompt">
              <Button variant="normal" onClick={() => sendMessage(prompt)}>
                {label}
              </Button>
            </div>
          ))}
        </div>
      </Box>
    );
  }

  return (
    <div>
      {state.messages.map((message) => (
        <CopilotMessageBubble key={message.id} message={message} />
      ))}
      {state.isLoading && (
        <Box padding={{ vertical: 's' }}>
          <Spinner size="normal" />{' '}
          <Box variant="span" color="text-body-secondary">
            Thinking...
          </Box>
        </Box>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
